import { useMemo } from 'react';
import type { InvestmentFlowPoint } from '@/types';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { EChart, type EChartsOption } from '@/components/charts/EChart';
import { formatBRLCompact } from '@/lib/charts/formatters';
import { denseAxisLabel } from '@/lib/charts/axis';
import { TOOLTIP_TEXT_HEADING, TOOLTIP_TEXT_MUTED, TOOLTIP_TEXT_STRONG } from '@/lib/charts/theme';
import { token } from '@/lib/theme/tokens';
import { useThemeVersion } from '@/lib/theme/useThemeColors';
import { SurfaceCard } from '@/components/ui/surface';

interface InvestmentsFlowChartProps {
  trends: InvestmentFlowPoint[];
  compact?: boolean;
  className?: string;
}

export function InvestmentsFlowChart({
  trends,
  compact = false,
  className = '',
}: InvestmentsFlowChartProps) {
  const formatCurrency = useFormatCurrency();
  const themeVersion = useThemeVersion();

  const totalNetFlow = useMemo(() => trends.reduce((s, t) => s + t.contributions - t.withdrawals, 0), [trends]);
  let netFlowSign = '';
  let netFlowClassName = 'text-text-secondary';
  if (totalNetFlow > 0) {
    netFlowSign = '+';
    netFlowClassName = 'text-amount-positive';
  } else if (totalNetFlow < 0) {
    netFlowSign = '-';
    netFlowClassName = 'text-amount-negative';
  }

  // Skip months with no movement (no contributions and no withdrawals).
  const activeTrends = useMemo(
    () => (trends ?? []).filter((t) => t.contributions !== 0 || t.withdrawals !== 0),
    [trends],
  );

  const option = useMemo<EChartsOption>(() => {
    if (activeTrends.length === 0) return {};
    const labels = activeTrends.map((t) => t.label);
    const aportes = activeTrends.map((t) => t.contributions);
    const retiradas = activeTrends.map((t) => t.withdrawals);
    const nets = aportes.map((a, i) => a - retiradas[i]);
    const placeholder: number[] = [];
    let running = 0;
    for (let i = 0; i < nets.length; i++) {
      const n = nets[i];
      placeholder.push(n >= 0 ? running : running + n);
      running += n;
    }

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (raw: unknown) => {
          const params = (Array.isArray(raw) ? raw : [raw]) as Array<{
            seriesName?: string;
            value?: number;
            color?: string;
            dataIndex?: number;
          }>;
          if (!params.length) return '';
          const idx = params[0].dataIndex ?? 0;
          const real = params.filter((p) => p.seriesName !== '_placeholder');
          let html = `<div style="min-width:160px;"><div style="font-size:11px;font-weight:600;color:${TOOLTIP_TEXT_HEADING};margin-bottom:4px;">${labels[idx]}</div>`;
          real.forEach((p) => {
            html += `<div style="display:flex;align-items:center;gap:8px;font-size:12px;color:${TOOLTIP_TEXT_MUTED};padding:1px 0;"><span style="width:8px;height:8px;background:${p.color};border-radius:50%;"></span><span style="flex:1;">${p.seriesName}</span><span style="color:${TOOLTIP_TEXT_STRONG};font-variant-numeric:tabular-nums;">${formatCurrency(p.value ?? 0)}</span></div>`;
          });
          html += '</div>';
          return html;
        },
      },
      legend: {
        data: ['Aportes', 'Resgates'],
        top: 0,
        right: 0,
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        textStyle: { fontWeight: 600 },
      },
      grid: { left: 4, right: 8, top: 28, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { fontWeight: 600, ...denseAxisLabel(labels.length, compact) },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => formatBRLCompact(v) },
        splitLine: { lineStyle: { color: token('--border-subtle', '#ECEDEC') } },
      },
      series: [
        {
          name: '_placeholder',
          type: 'bar',
          stack: 'wf',
          data: placeholder,
          itemStyle: { color: 'transparent', borderColor: 'transparent' },
          tooltip: { show: false },
          silent: true,
        },
        {
          name: 'Aportes',
          type: 'bar',
          data: aportes,
          // Signed-flow pairing (§01.6/§01.9): positive amount green for aportes,
          // neutral for resgates — the primary hue is never a data series.
          itemStyle: { color: token('--amount-positive', '#009054'), borderRadius: [3, 3, 0, 0] },
          animationDelay: (idx: number) => idx * 60,
          barGap: '10%',
        },
        {
          name: 'Resgates',
          type: 'bar',
          data: retiradas,
          itemStyle: { color: token('--neutral-300', '#E0E2E0'), borderRadius: [3, 3, 0, 0] },
          animationDelay: (idx: number) => idx * 60 + 80,
        },
      ],
    };
    // themeVersion: token() reads CSS custom properties at runtime — rebuild the
    // option on a theme flip or the bar/gridline colors go stale. formatCurrency:
    // privacy-aware tooltip amounts must re-mask when hide-values toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTrends, compact, themeVersion, formatCurrency]);

  if (activeTrends.length === 0) {
    return (
      <SurfaceCard className={`flex h-full flex-col p-4 sm:p-6 ${className}`} data-testid="investment-flow-card">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-text-primary">Fluxo Mensal</h4>
            <p className="mt-0.5 text-2xs text-text-secondary">Aportes vs resgates de capital</p>
          </div>
        </div>
        <div className={`flex flex-1 items-center justify-center text-sm text-text-secondary ${compact ? 'min-h-36' : 'min-h-40 sm:min-h-48'}`}>
          Sem dados disponíveis
        </div>
      </SurfaceCard>
    );
  }

  return (
    <SurfaceCard className={`flex h-full flex-col p-4 sm:p-6 ${className}`} data-testid="investment-flow-card">
      <div className={`mb-4 flex gap-3 ${compact ? 'items-start justify-between' : 'items-center justify-between'}`}>
        <div className="min-w-0">
          <h3 className={`${compact ? 'text-sm' : 'text-base'} truncate whitespace-nowrap font-semibold text-text-primary`}>Fluxo Mensal</h3>
          <p className={`${compact ? 'text-2xs' : 'text-xs'} mt-0.5 text-text-secondary`}>Aportes vs resgates de capital</p>
        </div>
        <div className={`flex shrink-0 flex-col items-end gap-0.5 font-mono font-semibold tabular-nums ${compact ? 'text-2xs' : 'text-2xs sm:text-xs'}`}>
          <span className={netFlowClassName}>
            {netFlowSign}{formatCurrency(Math.abs(totalNetFlow), { compact: true })}
          </span>
        </div>
      </div>
      <div className={compact ? 'h-40 min-h-40 flex-1' : 'h-44 min-h-44 flex-1 xs:h-48 xs:min-h-48 sm:h-56 sm:min-h-56'}>
        <EChart option={option} />
      </div>
    </SurfaceCard>
  );
}
